import { LotteryParser } from "@sazka/lotteries/js/utils/lottery-parser";
import { FlickityPatched } from "@sazka/utils/carousel/flickity-patched";
import { DataLayer } from "@sazka/utils/data-layer";
import { debounce } from "@sazka/utils/debounce";
import { onViewportEnter } from "@sazka/utils/viewport-enter";
import { AnalyticsHelper } from "@/lotteries/online/shared/analytics/analytics-helper";
import { ProductPageAnalytics } from "@/lotteries/product-page/analytics/analytics-product-page";
import { Analytics } from "@/shared/utils/analytics/analytics";
import { LotteryCheckout } from "../online/shared/lottery-checkout/lottery-checkout";

export class RandomBetSlider {
    private elem!: JQuery<Element>;
    private sliderAPI: any;
    private waitingWager: JQuery = null;
    private quickLotteries = false;
    private readonly checkout: LotteryCheckout = null;
    private flickity: FlickityPatched | any;

    constructor(querySelector: string, reverse = false, quickLotteries = false) {
        this.quickLotteries = quickLotteries;
        this.initSlider(querySelector, reverse);
        if (this.elem) {
            this.initRandomBets(this.elem.data("page-placement"));
        }
        this.checkout = new LotteryCheckout({
            key: "strip" + querySelector,
            useDefaultAnalytics: false,
            restartFlow: (_, restoreData) => {
                this.flickity.select(restoreData.index - 1, false, true);
                if (restoreData.type === "multiwager") {
                    const $btn = this.elem.find(
                        `.ds-quick-bet-card[data-lottery=multisazenka] .ds-quick-bet-card__button[data-wager-id=${restoreData.id}]`
                    );
                    this.setButtonConfirmState($btn);
                    const dataElems = $btn
                        .closest(".ds-quick-bet-card")
                        .find(".ds-quick-bet-card__list-item .inner-list-item");
                    this.sendMultiwager($btn, dataElems, restoreData.index);
                } else {
                    const $btn = this.elem.find(
                        `.ds-quick-bet-card[data-lottery!=multisazenka] .ds-quick-bet-card__button[data-wager-id=${restoreData.id}]`
                    );
                    this.setButtonConfirmState($btn);
                    this.sendWager($btn, restoreData.index);
                    $btn.trigger("focus");
                }
            },
        });
    }

    public initSlider = async (querySelector: string, reverse: boolean) => {
        const slideshowEl = document.querySelector(querySelector);
        if (!slideshowEl) return;
        this.elem = $(slideshowEl);

        await onViewportEnter(slideshowEl, 0.2);

        this.elem.parent().addClass("inited");

        const tickerSpeed = (reverse ? -1 : 1) * 0.5;

        let isPaused = false;
        let isInterruptedByUser = false;
        let lastWindowWidth = window.innerWidth;

        const update = () => {
            if (isPaused || isInterruptedByUser || this.waitingWager) return;
            if (this.flickity.slides) {
                this.flickity.x = (this.flickity.x - tickerSpeed) % this.flickity.slideableWidth;
                this.flickity.selectedIndex = this.flickity.dragEndRestingSelect();
                this.flickity.updateSelectedSlide();
                this.flickity.settle(this.flickity.x);
            }
            window.requestAnimationFrame(update);
        };

        const pause = () => {
            isPaused = true;
        };

        const play = () => {
            if (isPaused) {
                isPaused = false;
                window.requestAnimationFrame(update);
            }
        };

        const handleBlur = () => {
            if (this.waitingWager) return;
            else isInterruptedByUser = false;

            setTimeout(() => {
                //workaround for touch devices
                const $target = $(document.activeElement);
                if (!$target.hasClass(`ds-quick-bet-card__button`)) this.resetBetButtons();
            }, 1);

            window.requestAnimationFrame(update);
        };

        const $root = $(slideshowEl);
        const rootWidth = $root.outerWidth(true);
        const childWidth = $root.children().outerWidth(true);

        let totalWidth = $root.children().length * childWidth;
        while (totalWidth < rootWidth + 1000) {
            $root
                .children()
                .clone()
                .appendTo($root as any);
            totalWidth = $root.children().length * childWidth;
        }

        this.flickity = new FlickityPatched(slideshowEl, {
            autoPlay: false,
            pageDots: false,
            draggable: true,
            prevNextButtons: false,
            wrapAround: true,
            selectedAttraction: 0.015,
            friction: 0.25,
            percentPosition: false,
            resize: false,
            watchCSS: false,
        });
        this.flickity.x = 0;

        slideshowEl.addEventListener("mouseenter", pause, false);
        slideshowEl.addEventListener("focusin", pause, false);
        slideshowEl.addEventListener("mouseleave", play, false);
        slideshowEl.addEventListener("focusout", play, false);
        slideshowEl.addEventListener("blur", handleBlur, false);

        window.addEventListener(
            "resize",
            debounce(() => {
                if (window.innerWidth !== lastWindowWidth) {
                    this.flickity.resize();
                    lastWindowWidth = window.innerWidth;
                }
            }, 100),
            { passive: true }
        );

        update();

        this.sliderAPI = {
            pause: () => (isInterruptedByUser = true),
            play: () => (isInterruptedByUser = false),
        };
    };

    private resetBetButtons = () => {
        $(".ds-quick-bet-card__button").each((_, el: HTMLElement) => {
            const $this = $(el);

            $this
                .text(`Vsadit za ${$this.data("price")} Kč`)
                .removeClass("button--error")
                .addClass("button--primary");
        });
    };

    private initRandomBets = (pagePlacement: string) => {
        this.elem.addClass("inited");

        //change color by game type
        this.elem.find(".ds-quick-bet-card").each((index, el) => {
            const gameName = $(el).data("lottery");
            const $button = $(el).find(`.ds-quick-bet-card__button`);

            $button.on("click", () => {
                if ($button.hasClass("button--error")) {
                    if (gameName == "multisazenka") {
                        this.sendMultiwager($button, $(el).find(".ds-quick-bet-card__list-item"), index + 1);
                    } else {
                        this.sendWager($button, index + 1);
                    }
                } else {
                    this.resetBetButtons();
                    this.setButtonConfirmState($button);

                    if (pagePlacement == "Lotteries_PP_Bet") {
                        const gameCode =
                            gameName == "multisazenka"
                                ? "multisazenka"
                                : Analytics.getLotteryCode(LotteryParser.parseLotteryName(gameName));
                        const title = $(el)
                            .children(".ds-quick-bet-card__header")
                            .find(".ds-quick-bet-card__heading")
                            .text() as string;

                        ProductPageAnalytics.pushRandomTipClick(
                            gameCode,
                            $(el).index() + 1,
                            $button.data("price") as number,
                            title
                        );
                    }

                    if (gameName == "multisazenka") {
                        const wagerId = $button.data("wager-id");
                        const bettingMode = "multibets,randomTips";
                        AnalyticsHelper.pushMultiwagersCheckout(
                            $(el).find(".ds-quick-bet-card__list-item"),
                            index + 1,
                            wagerId,
                            bettingMode
                        );
                    } else {
                        this.datalayerCheckout($button);
                    }
                }
            });
        });

        this.elem.on("focus", () => this.sliderAPI.pause());
    };

    private setButtonConfirmState($btn: JQuery) {
        $btn.removeClass("button--primary").addClass("button--error").text("Opravdu vsadit?");
    }

    private sendWager = ($btn: JQuery, cardPosition: number) => {
        const wagerId = $btn.data("wager-id");
        const price = $btn.data("price");
        this.waitingWager = $btn;

        this.checkout
            .start({
                getCost: () => price,
                betServiceUrl: this.quickLotteries
                    ? `/random-wager/quick-lottery/${wagerId}`
                    : `/random-wager/${wagerId}`,
                dataForRestore: () => ({
                    type: "randomwager",
                    id: wagerId,
                    index: cardPosition,
                }),
            })
            .then((data: Record<string, any>) => {
                this.datalayerOrder(
                    $btn,
                    data.Payload.WagerID,
                    data.Payload[$btn.data("lottery")] == 1,
                    price,
                    this.quickLotteries ? "randomTipFastLotteries" : "randomTips"
                );
                this.onSuccess($btn);
            })
            .catch((error) => {
                console.error(error);
                this.resetBetButtons();
            })
            .finally(() => {
                this.waitingWager = null;
            });
    };

    private sendMultiwager = ($btn: JQuery, elementsWithData: JQuery, cardPosition: number) => {
        const header = $btn.closest(".ds-quick-bet-card").find(".ds-quick-bet-card__heading").text() as string;
        const wagerId = $btn.data("wager-id") as number;
        const price = $btn.data("price") as number;
        const bettingMode = "multibets,randomTips";
        this.waitingWager = $btn;

        this.checkout
            .start({
                betServiceUrl: `/multiwager/random-wager/${wagerId}`,
                getCost: () => price,
                dataForRestore: () => ({
                    type: "multiwager",
                    id: wagerId,
                    index: cardPosition,
                }),
            })
            .then((data: Record<string, any>) => {
                AnalyticsHelper.pushMultiwagerOrder(elementsWithData, data, wagerId, cardPosition, bettingMode);
                AnalyticsHelper.pushMultiwagerBetMoney(elementsWithData, header, cardPosition, wagerId);

                this.onSuccess($btn);
            })
            .catch((error) => {
                console.error(error);
                this.resetBetButtons();
            })
            .finally(() => {
                this.waitingWager = null;
            });
    };

    private onSuccess = ($btn: JQuery) => {
        const successOverlay = $btn.parent().find(".ds-quick-bet-card--success")[0];
        if (successOverlay) {
            successOverlay.style.display = "block";

            const seconds = $btn.data("success-seconds") as number;
            if (seconds > 0) {
                setTimeout(() => {
                    successOverlay.style.display = "none";
                }, seconds * 1000);
            }
        }
        this.resetBetButtons();
    };

    //#region Datalayer

    private datalayerOrder(button: JQuery, orderId: number, isFirstOrder: boolean, value: number, kind: string) {
        const obj = this.datalayerFromElement("purchase", button, orderId);
        DataLayer.push(obj);

        const parentCard = button.closest(".ds-quick-bet-card");
        const parentCardIndex = parentCard ? parentCard.index() : 0;

        this.datalayerBet(button.data("lottery"), value, kind, parentCardIndex + 1);
        if (isFirstOrder) {
            this.datalayerFirstBet(button.data("lottery"), value, kind);
        }
    }

    private datalayerCheckout(button: JQuery) {
        const obj = this.datalayerFromElement("checkout", button);
        DataLayer.push(obj);
    }

    private datalayerFromElement(type: "checkout" | "purchase", element: JQuery, orderId: number = null) {
        const gameName = element.data("lottery");
        const prize = parseInt(element.data("price"));
        const boards = element.data("boards") || 1;
        const duration = element.data("duration");
        const addon = element.data("addon") == "True";
        const addonName = addon ? LotteryParser.getAddon(gameName) : "";
        const ticketKind = duration > 1 ? "multidraw" : "singleDraw";
        const drawTimes = gameName == "kasicka" || gameName == "stastnych10" ? "aftevn" : "";
        let drawDays = "";
        switch (gameName) {
            case "eurojackpot":
                drawDays = "fr";
                break;
            case "sportka":
                drawDays = "wesu";
                break;
            case "euromiliony":
                drawDays = "tusa";
                break;
        }

        const kind = this.quickLotteries ? "randomTipFastLotteries" : "randomTips";

        return this.datalayerCart(
            type,
            gameName,
            prize,
            boards,
            duration,
            addonName,
            ticketKind,
            drawTimes,
            drawDays,
            orderId,
            kind
        );
    }

    private datalayerCart(
        type: "checkout" | "purchase",
        gameCode: "eurojackpot" | "sportka" | "euromiliony" | "kasicka" | "keno" | "stastnych10" | "extra-renta",
        cost: number,
        boards: number,
        draws: number,
        addonGameName: "sance" | "sancemh" | "extra6" | "eurosance" | "sancemilion" | "",
        ticketKind: "singleDraw" | "multidraw" | "sportkaMH",
        drawTimes: "aft" | "evn" | "aftevn" | "",
        drawDays: string,
        orderId: number,
        kind: string
    ) {
        const analyticsGameName = Analytics.getLotteryCode(gameCode);
        const cart = {
            products: [
                {
                    fullPrice: cost,
                    pocketPrice: cost,
                    price: cost,
                    tax: 0,
                    position: 1,
                    verticalCode: "loterie",
                    gameCode: analyticsGameName,
                    boards: boards,
                    draws: draws,
                    addonGame: addonGameName,
                    ticketKind: ticketKind,
                    drawDays: drawDays,
                    drawTimes: drawTimes,
                    bettingMode: kind,
                },
            ],
        };
        const obj = {
            page: {
                type: type,
                kind: kind == "mainProductPage" ? type : kind,
                gameCode: Analytics.lotteryCode(),
                gameCodeStrict: Analytics.getLotteryCode(LotteryParser.parseLotteryName(gameCode)),
                verticalCode: "loterie",
                verticalCodeStrict: "loterie",
                bettingSystem: draws > 1 ? "multiDraw" : "singleDraw",
            },
            order: {
                totalValue: cost,
                revenue: cost,
                discount: 0,
                tax: 0,
                cart: cart,
            },
            cart: cart,
            event: "page",
        };
        if (type === "checkout") {
            obj.page["checkout"] = { step: "1" };
        } else {
            obj.order["id"] = orderId;
        }

        return obj;
    }

    private datalayerBet(gameCodeName: string, value: number, kind: string, position: number) {
        const analyticsGameName = Analytics.getLotteryCode(gameCodeName);
        DataLayer.push({
            event: "action.bet.money",
            action: {
                bet: {
                    gameCode: analyticsGameName,
                    bettingMode: kind,
                    value,
                    position: position.toString(),
                },
            },
        });
    }

    private datalayerFirstBet(gameCode: string, value: number, kind: string) {
        DataLayer.push({
            event: "action.firstBet.money",
            action: {
                firstBet: {
                    bettingMode: kind,
                    gameCode: Analytics.getLotteryCode(gameCode),
                    value,
                },
            },
        });
    }
    //#endregion
}

$(() => {
    // dont start slider on /vsadit-si-online, coz its visible after bet
    if (
        "/loterie/klasicke-loterie/kasicka/jak-na-kombinovanou-sazku" !== environment.nodeAliasPath &&
        !/^\/loterie\/[\w-/]*\/vsadit-si-online(\/.*)?/i.test(environment.nodeAliasPath) &&
        !/^\/loterie\/multisazenky\/.+/i.test(location.pathname)
    ) {
        new RandomBetSlider(".random-wager__slider.normal .swiper-wrapper");
        new RandomBetSlider(".quick-wagers-slider", false, true);
    }
});
